import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, Zap, Flag, Navigation, Sun, Moon, RefreshCw } from 'lucide-react';

mapboxgl.accessToken = 'pk.eyJ1IjoibmFwYW5hcGFkIiwiYSI6ImNtaXZ1c243djJjamYzZ29ubTkwanpuaDcifQ.cb6rjMR4qJoseyans4BmTQ';

const haversine = (p1, p2) => {
  const R = 6371, toR = Math.PI / 180;
  const dLat = (p2.lat - p1.lat) * toR, dLon = (p2.lng - p1.lng) * toR;
  const a = Math.sin(dLat/2)**2 + Math.cos(p1.lat*toR)*Math.cos(p2.lat*toR)*Math.sin(dLon/2)**2;
  return R * 2 * Math.asin(Math.sqrt(a));
};

const parseGPX = (text) => {
  const xml = new DOMParser().parseFromString(text, 'application/xml');
  let nodes = Array.from(xml.getElementsByTagName('trkpt'));
  if (!nodes.length) nodes = Array.from(xml.getElementsByTagName('wpt'));
  const raw = nodes.map(n => ({
    lat: parseFloat(n.getAttribute('lat')),
    lng: parseFloat(n.getAttribute('lon')),
    elevation: parseFloat(n.getElementsByTagName('ele')[0]?.textContent || 0),
  }));
  let cum = 0;
  return raw.map((p, i) => ({ id: i+1, ...p, distance: parseFloat((i > 0 ? (cum += haversine(raw[i-1], p)) : 0).toFixed(2)), speed: 0 }));
};

const loadGPX = async () => {
  try {
    const res = await fetch('/route.gpx');
    if (!res.ok) throw new Error();
    return parseGPX(await res.text());
  } catch { return null; }
};

const getRiderPos = (progress, rt, totKm) => {
  if (!rt?.length || rt.length < 2) return { lat: 0, lng: 0, elevation: 0, distance: 0 };
  const idx = Math.min(Math.floor(progress * (rt.length-1)), rt.length-2);
  const t = progress * (rt.length-1) - idx;
  const lerp = (a, b) => a + (b - a) * t;
  return { lat: lerp(rt[idx].lat, rt[idx+1].lat), lng: lerp(rt[idx].lng, rt[idx+1].lng), elevation: lerp(rt[idx].elevation, rt[idx+1].elevation), distance: parseFloat((progress * (totKm||65)).toFixed(1)) };
};

const addRouteToMap = (map, coords) => {
  if (!map.getSource('route'))
    map.addSource('route', { type: 'geojson', lineMetrics: true, data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } } });
  if (!map.getLayer('route-glow'))
    map.addLayer({ id: 'route-glow', type: 'line', source: 'route', paint: { 'line-color': '#38bdf8', 'line-width': 10, 'line-opacity': 0.2, 'line-blur': 6 } });
  if (!map.getLayer('route-line'))
    map.addLayer({ id: 'route-line', type: 'line', source: 'route', paint: { 'line-color': '#38bdf8', 'line-width': 3, 'line-opacity': 0.9, 'line-gradient': ['interpolate', ['linear'], ['line-progress'], 0, '#38bdf8', 0.5, '#818cf8', 1, '#c084fc'] }, layout: { 'line-cap': 'round', 'line-join': 'round' } });
};

const getInitials = (name) => {
  const display = name.includes(' - ') ? name.split(' - ')[1] : name;
  return display.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
};

const getDisplayName = (name) => {
  if (name.includes(' - ')) return name.split(' - ')[0].trim();
  return name.split(' ')[0];
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const ElevTooltip = ({ active, payload, isDark }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: isDark ? 'rgba(5,10,20,0.95)' : 'rgba(255,255,255,0.97)', border: `1px solid ${isDark ? 'rgba(251,191,36,0.3)' : 'rgba(0,0,0,0.12)'}`, borderRadius: 8, padding: '8px 12px', fontSize: 11, color: isDark ? '#fff' : '#0f172a', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
      <div style={{ color: '#fbbf24', fontSize: 9, letterSpacing: 2, marginBottom: 4 }}>KM {d.distance}</div>
      <div style={{ fontWeight: 700, fontSize: 16 }}>{d.elevation.toFixed(0)}<span style={{ fontSize: 10, fontWeight: 400, color: '#64748b' }}>m</span></div>
      <div style={{ color: '#64748b', fontSize: 10, marginTop: 2 }}>{d.speed.toFixed(1)} km/h</div>
    </div>
  );
};

const StatBox = ({ label, value, color, dark }) => (
  <div style={{ background: dark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)', padding: '4px 7px', borderRadius: 4 }}>
    <div style={{ fontSize: 7, color: '#475569', letterSpacing: 1 }}>{label}</div>
    <div style={{ fontSize: 11, color, fontWeight: 700 }}>{value}</div>
  </div>
);

const DetailRow = ({ icon, label, value, color, dark }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', fontSize: 9 }}>{icon}{label}</div>
    <div style={{ fontSize: 11, color, fontWeight: 700 }}>{value}</div>
  </div>
);

export default function InteractiveRoute({ trackers = [], isDark = true, onToggleDark, onRefresh, loading = false }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const hoverMarker = useRef(null);
  const riderMarkers = useRef({});
  const routeRef = useRef([]);
  const isFollowingRider = useRef(false);
  const isDraggingChart = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(165);
  const isFirstRender = useRef(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hoverPoint, setHoverPoint] = useState(null);
  const [selectedRider, setSelectedRider] = useState(null);
  const [chartHeight, setChartHeight] = useState(165);
  const [route, setRoute] = useState([]);
  const [totalKm, setTotalKm] = useState(65);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const startMarkerRef = useRef(null);
  const finishMarkerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [elevChartOpen, setElevChartOpen] = useState(false);


  useEffect(() => {
    loadGPX().then(pts => {
      if (!pts?.length) return;
      routeRef.current = pts;
      setRoute(pts);
      setTotalKm(parseFloat(pts[pts.length-1].distance));
    });
  }, []);

  const riders = trackers.map(t => ({
    id: t.id, name: t.name, color: t.color,
    livePos: t.livePosition ? { lat: t.livePosition.lat, lng: t.livePosition.lng } : null,
    progress: Math.min((parseFloat(t.totalDistance)||0) / totalKm, 1),
    speed: t.livePosition?.speed || 0,
    status: t.status,
    lastUpdate: t.lastUpdate || null,
  }));
  const ranked = [...riders].sort((a, b) => b.progress - a.progress);

  useEffect(() => {
    if (map.current || !mapContainer.current) return;
    map.current = new mapboxgl.Map({ container: mapContainer.current, style: isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/outdoors-v12', center: [106.826, -6.4], zoom: 9.5 });
    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.current.on('load', () => { addRouteToMap(map.current, routeRef.current.map(p => [p.lng, p.lat])); setMapLoaded(true); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
      if (!mapLoaded || !route.length) return;
      const popupBg = isDark ? '#0f172a' : '#ffffff';
      const popupText = isDark ? '#fff' : '#0f172a';
      startMarkerRef.current?.remove();
      finishMarkerRef.current?.remove();

      startMarkerRef.current = new mapboxgl.Marker({ color: '#10b981' })
      .setLngLat([route[0].lng, route[0].lat])
      .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(`
        <div style="background:${popupBg};border-radius:6px;padding:6px 10px;font-family:monospace;border-left:3px solid #10b981;box-shadow:0 4px 12px rgba(0,0,0,0.4);">
          <div style="font-size:11px;font-weight:700;color:${popupText};">🚦 START</div>
        </div>
      `))
      .addTo(map.current);
      finishMarkerRef.current = new mapboxgl.Marker({ color: '#ef4444' })
      .setLngLat([route[route.length-1].lng, route[route.length-1].lat])
      .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(`
        <div style="background:${popupBg};border-radius:6px;padding:6px 10px;font-family:monospace;border-left:3px solid #ef4444;box-shadow:0 4px 12px rgba(0,0,0,0.4);">
          <div style="font-size:11px;font-weight:700;color:${popupText};">🏁 FINISH</div>
        </div>
      `))
      .addTo(map.current);
  }, [route, mapLoaded, isDark]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    map.current.setStyle(isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/outdoors-v12');
    map.current.once('style.load', () => addRouteToMap(map.current, routeRef.current.map(p => [p.lng, p.lat])));
  }, [isDark, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded) return;
    riders.forEach(rider => {
      if (!rider.livePos && trackers.length > 0) return;
      const pos = rider.livePos ?? getRiderPos(rider.progress, route, totalKm);
      if (riderMarkers.current[rider.id]) { riderMarkers.current[rider.id].setLngLat([pos.lng, pos.lat]); return; }
      const el = document.createElement('div');
      el.style.cssText = 'width:40px;height:40px;cursor:pointer;';
      el.innerHTML = `
        <div style="position:absolute;inset:0;border-radius:50%;background:${rider.color}33;animation:riderPulse 2s ease-in-out infinite;"></div>
        <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:24px;height:24px;border-radius:50%;background:${rider.color};border:2.5px solid #fff;box-shadow:0 0 14px ${rider.color}99;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;color:#fff;font-family:monospace;">${getInitials(rider.name)}</div>
        <div style="position:absolute;bottom:-20px;left:50%;transform:translateX(-50%);background:${rider.color};color:#fff;padding:2px 6px;border-radius:4px;font-size:8px;font-family:monospace;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${getDisplayName(rider.name)}</div>
      `;
      el.addEventListener('click', () => setSelectedRider(r => r?.id === rider.id ? null : rider));
      riderMarkers.current[rider.id] = new mapboxgl.Marker({ element: el, anchor: 'center' }).setLngLat([pos.lng, pos.lat]).addTo(map.current);
    });
    Object.keys(riderMarkers.current).forEach(id => {
      if (!riders.find(r => r.id === id)) { riderMarkers.current[id].remove(); delete riderMarkers.current[id]; }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, riders]);

  useEffect(() => {
    if (!mapLoaded) return;
    if (hoverPoint) {
      if (!hoverMarker.current) {
        const el = document.createElement('div');
        el.style.cssText = 'width:18px;height:18px;border-radius:50%;background:#fbbf24;border:3px solid #fff;box-shadow:0 0 20px rgba(251,191,36,0.9),0 0 0 6px rgba(251,191,36,0.2);pointer-events:none;';
        hoverMarker.current = new mapboxgl.Marker({ element: el, anchor: 'center' }).setLngLat([hoverPoint.lng, hoverPoint.lat]).addTo(map.current);
      } else {
        hoverMarker.current.setLngLat([hoverPoint.lng, hoverPoint.lat]);
      }
      if (!isFollowingRider.current) map.current.easeTo({ center: [hoverPoint.lng, hoverPoint.lat], duration: 250, zoom: 13 });
    } else {
      hoverMarker.current?.remove();
      hoverMarker.current = null;
      if (!isFollowingRider.current && route.length) {
        const lngs = route.map(p => p.lng), lats = route.map(p => p.lat);
        map.current.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 60, duration: 800 });
      }
    }
  }, [hoverPoint, mapLoaded, route]);

  const handleChartMove = useCallback((data) => {
    if (data?.activePayload?.[0]) setHoverPoint(data.activePayload[0].payload);
    else if (data?.activeTooltipIndex !== undefined && route[data.activeTooltipIndex]) setHoverPoint(route[data.activeTooltipIndex]);
  }, [route]);
  const handleChartLeave = useCallback(() => setHoverPoint(null), []);

  const onChartDragStart = useCallback((e) => {
    isDraggingChart.current = true;
    dragStartY.current = e.clientY;
    dragStartHeight.current = chartHeight;
    e.preventDefault();
  }, [chartHeight]);

  useEffect(() => {
    const onMove = (e) => { if (isDraggingChart.current) setChartHeight(Math.min(Math.max(dragStartHeight.current + (dragStartY.current - e.clientY), 80), 500)); };
    const onUp = () => { isDraggingChart.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  const flyToRider = (r, isSelected) => {
    if (!map.current) return;
    if (isSelected) { isFollowingRider.current = false; return; }
    isFollowingRider.current = true;
    const center = r.livePos ? [r.livePos.lng, r.livePos.lat] : (() => { const p = getRiderPos(r.progress, route, totalKm); return [p.lng, p.lat]; })();
    map.current.flyTo({ center, zoom: 13, duration: 1000, essential: true });
    map.current.once('moveend', () => { isFollowingRider.current = false; });
  };

  const dark = isDark;
  const bd = dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';

  const filteredRanked = ranked.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
  <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: dark ? '#050a14' : '#f1f5f9', fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif", overflow: 'hidden' }}>
    <style>{`
      *{box-sizing:border-box;margin:0;padding:0}
      ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-thumb{background:#1e3a5f;border-radius:2px}
      .rcard{transition:all .18s ease;cursor:pointer} .rcard:hover{transform:translateX(3px)}
      .icon-btn{display:flex;align-items:center;justify-content:center;gap:5px;border-radius:6px;padding:6px 10px;cursor:pointer;font-family:inherit;font-size:11px;transition:all .15s;border:none}
      .icon-btn:hover{opacity:.8;transform:scale(1.05)}
      @keyframes riderPulse{0%,100%{transform:scale(1);opacity:.6}50%{transform:scale(1.5);opacity:.15}}
      @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
      .mapboxgl-popup-content{background:transparent!important;padding:0!important;box-shadow:none!important;}
      .mapboxgl-popup-tip{display:none!important;}
      .mapboxgl-popup-close-button{display:none!important;}
      .bottom-sheet{position:fixed;bottom:0;left:0;right:0;z-index:50;border-radius:16px 16px 0 0;transition:transform 0.3s ease;touch-action:none;}
      .bottom-sheet-open{transform:translateY(0);}
      .bottom-sheet-closed{transform:translateY(calc(100% - 48px));}
    `}</style>

    {/* HEADER */}
    <div style={{ padding: '8px 16px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14, background: dark ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)', borderBottom: `1px solid ${bd}`, backdropFilter: 'blur(12px)', zIndex: 10 }}>
      <div>
        <div style={{ fontSize: isMobile ? 14 : 20, letterSpacing: 3, color: dark ? '#FBBF24' : '#0f172a', lineHeight: 1 }}>GEEKBIKE RACE</div>
        <div style={{ fontSize: 8, color: '#64748b', letterSpacing: 2 }}>LIVE TRACKER</div>
      </div>
      <div style={{ width: 1, height: 30, background: bd }} />
      <div style={{ fontSize: 10, color: '#64748b' }}>{riders.length} <span style={{ color: '#38bdf8' }}>RIDERS</span></div>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        {/* Tombol elevation di mobile */}
        {isMobile && (
          <button className="icon-btn" onClick={() => setElevChartOpen(o => !o)} style={{ background: dark ? 'rgba(56,189,248,0.15)' : 'rgba(56,189,248,0.1)', border: `1px solid rgba(56,189,248,0.3)`, color: '#38bdf8' }}>
            <TrendingUp size={13} />{elevChartOpen ? 'HIDE' : 'ELEV'}
          </button>
        )}
        {onToggleDark && (
          <button className="icon-btn" onClick={onToggleDark} style={{ background: dark ? 'rgba(251,191,36,0.15)' : 'rgba(99,102,241,0.1)', border: `1px solid ${dark ? 'rgba(251,191,36,0.3)' : 'rgba(99,102,241,0.2)'}`, color: dark ? '#fbbf24' : '#6366f1' }}>
            {dark ? <Sun size={14} /> : <Moon size={14} />}{!isMobile && (dark ? 'LIGHT' : 'DARK')}
          </button>
        )}
        {onRefresh && (
          <button className="icon-btn" onClick={onRefresh} style={{ background: dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, color: dark ? '#94a3b8' : '#64748b' }}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        )}
      </div>
    </div>

    {/* BODY */}
    <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>

      {/* SIDEBAR — desktop only */}
      {!isMobile && (
        <div style={{ width: sidebarOpen ? 210 : 36, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${bd}`, background: dark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.7)', zIndex: 5, transition: 'width 0.25s ease', overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', fontSize: 8, letterSpacing: 2, color: '#475569', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', whiteSpace: 'nowrap' }}>
            {sidebarOpen && 'LEADERBOARD'}
            <button onClick={() => { setSidebarOpen(o => !o); setTimeout(() => map.current?.resize(), 260); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', marginLeft: sidebarOpen ? 'auto' : 0 }}>
              {sidebarOpen ? '◀' : '▶'}
            </button>
          </div>
          {sidebarOpen && (
            <>
              <div style={{ padding: '6px 8px', borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}` }}>
                <input type="text" placeholder="Search rider..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  style={{ width: '100%', background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 5, padding: '5px 8px', fontSize: 10, color: dark ? '#e2e8f0' : '#0f172a', outline: 'none', fontFamily: 'inherit', letterSpacing: 0.5 }} />
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                {filteredRanked.length === 0 && <div style={{ fontSize: 9, color: '#475569', textAlign: 'center', marginTop: 16 }}>No rider found</div>}
                {filteredRanked.map((r) => {
                  const isSelected = selectedRider?.id === r.id;
                  const pos = getRiderPos(r.progress, route, totalKm);
                  return (
                    <div key={r.id} className="rcard" onClick={() => { setSelectedRider(isSelected ? null : r); flyToRider(r, isSelected); }}
                      style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 6, background: isSelected ? `${r.color}18` : dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isSelected ? r.color+'55' : 'transparent'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 18, color: dark ? '#fff' : '#0f172a', lineHeight: 1, fontWeight: 700 }}>#{r.originalRank}</span>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, boxShadow: `0 0 8px ${r.color}` }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: dark ? '#e2e8f0' : '#1e293b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                          <div style={{ fontSize: 8, color: r.status === 'online' ? '#10b981' : '#ef4444' }}>{r.status === 'online' ? '● ONLINE' : '● OFFLINE'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                        <StatBox label="DIST" value={`${pos.distance}km`} color={r.color} dark={dark} />
                        <StatBox label="SPD" value={r.speed.toFixed(1)} color={dark ? '#e2e8f0' : '#0f172a'} dark={dark} />
                      </div>
                      <div style={{ marginTop: 7, height: 2, background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)', borderRadius: 1, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${r.progress*100}%`, background: r.color, boxShadow: `0 0 6px ${r.color}`, transition: 'width 0.5s' }} />
                      </div>
                      <div style={{ fontSize: 7, color: '#475569', marginTop: 2, textAlign: 'right' }}>{(r.progress*100).toFixed(0)}%</div>
                      {r.lastUpdate && (
                        <div style={{ fontSize: 7, color: '#475569', marginTop: 4, borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`, paddingTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ letterSpacing: 0.5 }}>LAST UPDATE</span>
                          <span style={{ color: dark ? '#94a3b8' : '#64748b' }}>
                            {(() => { const d = new Date(r.lastUpdate); const pad = n => String(n).padStart(2,'0'); return `${pad(d.getDate())}/${pad(d.getMonth()+1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; })()}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* MAP + CHART */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
          <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />

          {hoverPoint && (
            <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 10, pointerEvents: 'none', background: dark ? 'rgba(5,10,20,0.92)' : 'rgba(255,255,255,0.95)', border: `1px solid ${dark ? 'rgba(251,191,36,0.35)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 10, padding: '10px 14px', minWidth: 155, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
              <div style={{ fontSize: 8, color: '#fbbf24', letterSpacing: 2, marginBottom: 5 }}>📍 RUTE POINTER</div>
              <div style={{ fontSize: 24, color: dark ? '#fff' : '#0f172a', lineHeight: 1, fontWeight: 700 }}>{hoverPoint.elevation.toFixed(0)}<span style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>m</span></div>
              <div style={{ fontSize: 9, color: '#38bdf8', marginTop: 3, fontWeight: 600 }}>KM {hoverPoint.distance}</div>
              <div style={{ fontSize: 9, color: dark ? '#94a3b8' : '#475569', marginTop: 4 }}>{hoverPoint.lat.toFixed(5)}, {hoverPoint.lng.toFixed(5)}</div>
            </div>
          )}

          {selectedRider && (() => {
            const pos = getRiderPos(selectedRider.progress, route, totalKm);
            return (
              <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 10, background: dark ? 'rgba(5,10,20,0.92)' : 'rgba(255,255,255,0.95)', border: `1px solid ${selectedRider.color}55`, borderRadius: 10, padding: '10px 14px', minWidth: 165, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 8, color: selectedRider.color, letterSpacing: 2 }}>RIDER DETAIL</div>
                  <button onClick={() => setSelectedRider(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: selectedRider.color+'22', border: `2px solid ${selectedRider.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: selectedRider.color, fontWeight: 700 }}>
                    {getInitials(selectedRider.name)}
                  </div>
                  <div style={{ fontSize: 12, color: dark ? '#e2e8f0' : '#0f172a', fontWeight: 700 }}>{selectedRider.name}</div>
                </div>
                <DetailRow icon={<Zap size={10}/>} label="SPEED" value={`${selectedRider.speed.toFixed(1)} km/h`} color={selectedRider.color} dark={dark} />
                <DetailRow icon={<Navigation size={10}/>} label="DISTANCE" value={`${pos.distance} km`} color={selectedRider.color} dark={dark} />
                <DetailRow icon={<TrendingUp size={10}/>} label="ELEVATION" value={`${pos.elevation.toFixed(0)} m`} color={selectedRider.color} dark={dark} />
                <DetailRow icon={<Flag size={10}/>} label="PROGRESS" value={`${(selectedRider.progress*100).toFixed(1)}%`} color={selectedRider.color} dark={dark} />
              </div>
            );
          })()}
        </div>

        {/* ELEVATION CHART — desktop always show, mobile toggle */}
        {(!isMobile || elevChartOpen) && (
          <div style={{ height: isMobile ? 180 : chartHeight, flexShrink: 0, position: 'relative', borderTop: `1px solid ${bd}`, background: dark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)' }}>
            {!isMobile && (
              <div onMouseDown={onChartDragStart} style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 10, cursor: 'ns-resize', zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)' }} />
              </div>
            )}
            <div style={{ padding: '14px 16px 4px' }}>
              <div style={{ fontSize: 8, letterSpacing: 2, color: '#475569', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                <TrendingUp size={10} style={{ color: '#38bdf8' }} />
                ELEVATION PROFILE
                {hoverPoint && <span style={{ color: '#fbbf24' }}>── {hoverPoint.elevation.toFixed(0)}m @ KM {hoverPoint.distance}</span>}
                {isMobile && <button onClick={() => setElevChartOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 16 }}>×</button>}
              </div>
              <div style={{ height: (isMobile ? 180 : chartHeight) - 47, cursor: 'crosshair' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={route} onMouseMove={handleChartMove} onMouseLeave={handleChartLeave} onClick={handleChartMove} margin={{ top: 4, right: 8, bottom: 0, left: 28 }}>
                    <defs>
                      <linearGradient id="elevGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 6" stroke={dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'} vertical={false} />
                    <XAxis dataKey="distance" stroke="transparent" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} tickFormatter={v => `${v}km`} interval="preserveStartEnd" />
                    <YAxis stroke="transparent" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} tickFormatter={v => `${v}m`} width={26} />
                    <Tooltip content={<ElevTooltip isDark={dark} />} cursor={{ stroke: 'rgba(251,191,36,0.5)', strokeWidth: 1, strokeDasharray: '4 2' }} />
                    {riders.filter(r => r.status === 'online').map(r => (
                      <ReferenceLine key={r.id} x={getRiderPos(r.progress, route, totalKm).distance} stroke={r.color} strokeWidth={1.5} strokeDasharray="3 3" label={{ value: getDisplayName(r.name), fill: r.color, fontSize: 8, position: 'top' }} />
                    ))}
                    {hoverPoint && <ReferenceLine x={hoverPoint.distance} stroke="#fbbf24" strokeWidth={1.5} />}
                    <Area type="monotone" dataKey="elevation" stroke="#38bdf8" strokeWidth={2} fillOpacity={1} fill="url(#elevGrad)" dot={false} activeDot={{ r: 5, fill: '#fbbf24', stroke: '#fff', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM SHEET — mobile only */}
      {isMobile && (
        <div className={`bottom-sheet ${bottomSheetOpen ? 'bottom-sheet-open' : 'bottom-sheet-closed'}`}
          style={{ background: dark ? 'rgba(5,10,20,0.97)' : 'rgba(255,255,255,0.98)', borderTop: `1px solid ${bd}`, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
          
          {/* Handle / toggle */}
          <div onClick={() => setBottomSheetOpen(o => !o)} style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderBottom: `1px solid ${bd}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', margin: '0 auto' }} />
            </div>
            <div style={{ fontSize: 8, letterSpacing: 2, color: '#475569' }}>LEADERBOARD — {riders.length} RIDERS</div>
            <div style={{ fontSize: 10, color: '#475569' }}>{bottomSheetOpen ? '▼' : '▲'}</div>
          </div>

          {/* Search */}
          <div style={{ padding: '6px 12px', borderBottom: `1px solid ${bd}` }}>
            <input type="text" placeholder="Search rider..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={{ width: '100%', background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 5, padding: '6px 10px', fontSize: 12, color: dark ? '#e2e8f0' : '#0f172a', outline: 'none', fontFamily: 'inherit' }} />
          </div>

          {/* Rider list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {filteredRanked.length === 0 && <div style={{ fontSize: 11, color: '#475569', textAlign: 'center', marginTop: 16 }}>No rider found</div>}
            {filteredRanked.map((r) => {
              const isSelected = selectedRider?.id === r.id;
              const pos = getRiderPos(r.progress, route, totalKm);
              return (
                <div key={r.id} className="rcard" onClick={() => { setSelectedRider(isSelected ? null : r); flyToRider(r, isSelected); setBottomSheetOpen(false); }}
                  style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 8, background: isSelected ? `${r.color}18` : dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isSelected ? r.color+'55' : 'transparent'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 20, color: dark ? '#fff' : '#0f172a', lineHeight: 1, fontWeight: 700 }}>#{r.originalRank}</span>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: r.color, boxShadow: `0 0 8px ${r.color}` }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: dark ? '#e2e8f0' : '#1e293b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                      <div style={{ fontSize: 10, color: r.status === 'online' ? '#10b981' : '#ef4444' }}>{r.status === 'online' ? '● ONLINE' : '● OFFLINE'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <StatBox label="DIST" value={`${pos.distance}km`} color={r.color} dark={dark} />
                    <StatBox label="SPD" value={r.speed.toFixed(1)} color={dark ? '#e2e8f0' : '#0f172a'} dark={dark} />
                  </div>
                  <div style={{ marginTop: 8, height: 3, background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${r.progress*100}%`, background: r.color, transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ fontSize: 9, color: '#475569', marginTop: 3, textAlign: 'right' }}>{(r.progress*100).toFixed(0)}%</div>
                  {r.lastUpdate && (
                    <div style={{ fontSize: 9, color: '#475569', marginTop: 5, borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`, paddingTop: 5, display: 'flex', justifyContent: 'space-between' }}>
                      <span>LAST UPDATE</span>
                      <span style={{ color: dark ? '#94a3b8' : '#64748b' }}>
                        {(() => { const d = new Date(r.lastUpdate); const pad = n => String(n).padStart(2,'0'); return `${pad(d.getDate())}/${pad(d.getMonth()+1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; })()}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  </div>
);
}
